import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useApiClient } from 'clients/yogi-api'
import { useYogiQLRegistry } from 'features/charts-v2/hooks/use-yogiql-registry'
import { YogiQLChartsService } from 'features/charts-v2/service/yogiql-service'
import { type Chart, type TimeBucketOption } from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { buildChartRequestPanel } from 'features/charts-v2/utils/build-chart-request-panel'
import { runYogiQLChartQuery } from 'features/charts-v2/utils/run-yogiql-chart-query'
import {
  type YogiQLNames,
  EMPTY_YOGIQL_NAMES,
} from 'features/charts-v2/utils/to-yogiql-chart-request'

interface UseChartQueryResult {
  data: Chart | null
  /** True only while there is nothing to show yet. With `keepPreviousData`,
   *  a refetch for a new panel keeps the previous chart and leaves this
   *  false — read `isFetching` for "a request is in flight". */
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => Promise<Chart | null>
  /** Under `forceYogiQL`: the field catalog failed, so the chart cannot be
   *  drawn and nothing was queried. */
  yogiqlUnavailable: boolean
}

// Helper: Sort chart data by time
function sortChartDataIfNeeded(
  chartData: Chart,
  timeBucket: TimeBucketOption | null,
): Chart {
  if (!timeBucket || !chartData?.data) {
    return chartData
  }

  const sortedChartData = { ...chartData }
  sortedChartData.data = [...chartData.data].sort((a, b) => {
    const timeA = a.category?.[0]
    const timeB = b.category?.[0]

    if (!timeA || !timeB) return 0

    const dateA = new Date(timeA)
    const dateB = new Date(timeB)

    return dateA.getTime() - dateB.getTime()
  })

  return sortedChartData
}

export function useChartQuery(
  projectId: string | null,
  panel: ChartPanel | null,
  options?: {
    enabled?: boolean
    keepPreviousData?: boolean
    /** Opt in to the YogiQL route (charts page only); needs the flag too. */
    yogiql?: boolean
    yogiqlNames?: YogiQLNames
    /** With `yogiql`: the YogiQL route whatever the hub flag says, and never
     *  the legacy one (Ask Yogi's chat charts). Waits for the catalog only. */
    forceYogiQL?: boolean
  },
): UseChartQueryResult {
  const client = useApiClient()

  // The YogiQL branch: only when the hub flag is on AND the caller opted in.
  const yogiqlService = useMemo(() => new YogiQLChartsService(client), [client])
  const {
    enhancedDialect,
    flagSettled,
    catalog,
    settled: registrySettled,
  } = useYogiQLRegistry(
    yogiqlService,
    projectId,
    options?.yogiql === true,
    options?.forceYogiQL === true,
  )
  const forced = options?.yogiql === true && options.forceYogiQL === true
  const yogiqlEnabled = (enhancedDialect || forced) && options?.yogiql === true
  const yogiqlNames = options?.yogiqlNames ?? EMPTY_YOGIQL_NAMES
  const yogiqlReady = yogiqlEnabled && !!catalog
  // A caller that opted in waits for the flag and, if it is on, for the
  // field catalog to settle (success or error), so a flagged page load
  // runs one chart query rather than legacy first and YogiQL after.
  const yogiqlGateSettled = forced
    ? registrySettled && !!catalog
    : !options?.yogiql || (flagSettled && (!enhancedDialect || registrySettled))
  const stableYogiQLNames = JSON.stringify(yogiqlNames)

  // Stable stringified versions of the request-affecting panel fields. They
  // form the query key, so the chart refetches only when something that changes
  // the request changes — cosmetic panel fields (font size, legend, zones, …)
  // never trigger a fetch. Recomputing equal strings each render is harmless:
  // react-query hashes the key by value, so an unchanged key does not refetch.
  const stableFilters = JSON.stringify(panel?.filters ?? [])
  const stableDateRange = JSON.stringify({
    start: panel?.dateRange.startDate,
    end: panel?.dateRange.endDate,
  })
  const stableMetrics = JSON.stringify(panel?.metrics ?? [])
  const stableBreakdowns = JSON.stringify(panel?.breakdowns ?? [])
  const stableSearchTerms = JSON.stringify(panel?.searchTerms ?? [])
  const stableSearchCriteria = JSON.stringify(panel?.searchCriteria ?? [])

  const queryKey = [
    'charts-v2-chart',
    projectId,
    stableFilters,
    stableDateRange,
    stableMetrics,
    stableBreakdowns,
    panel?.timeBucket ?? null,
    panel?.chartType ?? null,
    stableSearchTerms,
    stableSearchCriteria,
    panel?.smartSearchEnabled ?? null,
    yogiqlReady,
    // Only what the request reads: the names change nothing on the legacy route.
    yogiqlReady ? stableYogiQLNames : null,
    // Forced never falls back to legacy, so a panel YogiQL cannot encode has a
    // different answer here than on the page: the two must not share a cache.
    forced,
  ]

  const { data, isLoading, isFetching, error, refetch } = useQuery<
    Chart | null,
    Error
  >(
    queryKey,
    async () => {
      // enabled guards this, but the closure must satisfy the type checker.
      if (!projectId || !panel) return null

      // No sortChartDataIfNeeded here: the server's default sort orders the
      // row breakdowns ascending with a line's time last (07 §8).
      if (yogiqlReady) {
        const chart = await runYogiQLChartQuery(
          yogiqlService,
          projectId,
          panel,
          catalog,
          yogiqlNames,
        )
        if (chart) return chart
      }
      // Forced: an unencodable panel is an error, never a legacy query.
      if (forced) throw new Error('the panel does not encode as YogiQL')

      // The legacy route's lowering (weighting metrics, legacy-search heal,
      // SEMANTIC marker, query-field projection) lives in one helper, which
      // a dashboard tile's Ask Yogi snapshot also reports.
      //
      // Posted as-is. The panel this hook holds IS the persisted model, and it
      // goes to the wire under the names it is stored under — no vocabulary
      // fetch, no rename at the boundary. That rename was not idempotent
      // (`product` is a legacy spelling of one column and the canonical name of
      // another), so the panel could only ever be converted exactly once, at
      // exactly this point, and never converted back. Sending the stored shape
      // is what lets a caller reason about, and edit, one model rather than two.
      const response = await client.post(
        `/charts/chart?proj_uuid=${projectId}`,
        { panel: buildChartRequestPanel(panel) },
      )

      const sorted = sortChartDataIfNeeded(
        response.data as Chart,
        panel.timeBucket,
      )
      return sorted
    },
    {
      // Caller may force-disable the query (e.g. a deprecated v1 chart that
      // must not hit /charts/chart); default preserves the original behavior.
      enabled:
        (options?.enabled ?? true) &&
        !!projectId &&
        !!panel &&
        yogiqlGateSettled,
      refetchOnWindowFocus: false,
      // Opt-in: keep showing the last chart while a changed panel refetches,
      // so a caller that swaps its whole UI for a loading state on
      // `isLoading` (a dashboard tile) doesn't unmount on every edit.
      keepPreviousData: options?.keepPreviousData ?? false,
    },
  )

  return {
    data: data ?? null,
    isLoading,
    isFetching,
    error: error ?? null,
    yogiqlUnavailable: forced && registrySettled && !catalog,
    refetch: async () => {
      const result = await refetch()
      return result.data ?? null
    },
  }
}
